import { useLayoutEffect, type RefObject } from "react";

/**
 * Fit a textarea's height to its text up to `maxHeight`, then scroll. Re-fits
 * when `value` changes and when the field's width changes (wrapping).
 */
export function useAutoResizeTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: unknown,
  maxHeight: number,
) {
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const resize = () => {
      element.style.height = "auto";
      const contentHeight = element.scrollHeight;
      element.style.height = `${Math.min(contentHeight, maxHeight)}px`;
      element.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden";
    };
    resize();
    if (typeof ResizeObserver === "undefined") return;
    let width = element.clientWidth;
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) return;
      width = element.clientWidth;
      resize();
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, value, maxHeight]);
}
